import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  TextField, 
  Button, 
  Grid, 
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  InputAdornment,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tab,
  Tabs,
  CircularProgress,
  Chip,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  CardActions,
  Divider
} from '@mui/material';
import { 
  Search as SearchIcon, 
  Add as AddIcon,
  Edit as EditIcon,
  QrCode as ScanIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// Data interface
interface InventoryItem {
  id: string;
  sku: string;
  name: string;
  category: string;
  price: number;
  cost: number;
  quantity: number;
  reorderPoint: number;
  location: string;
}

const InventoryPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [stockFilter, setStockFilter] = useState('all');
  const [tabValue, setTabValue] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentItem, setCurrentItem] = useState<InventoryItem | null>(null);
  const [inventory, setInventory] = useState<InventoryItem[]>([]);

  // Categories
  const categories = ['Apparel', 'Food', 'Drinks', 'Fishing', 'Boating', 'Souvenirs'];

  useEffect(() => {
    // In a real implementation, you would fetch inventory data from your database here
    // For now, we're initializing with an empty array
    setLoading(false);
  }, []);

  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Filter inventory based on search, category, and stock level
  const filteredInventory = inventory.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          item.sku.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = categoryFilter === 'all' || item.category === categoryFilter;
    
    const matchesStock = stockFilter === 'all' || 
                         (stockFilter === 'low' && item.quantity <= item.reorderPoint) ||
                         (stockFilter === 'out' && item.quantity === 0) ||
                         (stockFilter === 'in' && item.quantity > 0);
    
    return matchesSearch && matchesCategory && matchesStock;
  });

  // Open dialog to edit an item
  const handleEditItem = (item: InventoryItem) => {
    setCurrentItem(item);
    setOpenDialog(true);
  };

  // Open dialog to add a new item
  const handleAddItem = () => {
    setCurrentItem({
      id: '',
      sku: '',
      name: '',
      category: categories[0],
      price: 0,
      cost: 0,
      quantity: 0,
      reorderPoint: 5,
      location: ''
    });
    setOpenDialog(true);
  };

  // Handle dialog save
  const handleSaveItem = () => {
    if (currentItem) {
      if (currentItem.id) {
        // Update existing item
        setInventory(prevInventory => 
          prevInventory.map(item => 
            item.id === currentItem.id ? currentItem : item
          )
        );
      } else {
        // Add new item
        const newItem = {
          ...currentItem,
          id: `item-${inventory.length + 1}`
        };
        setInventory(prevInventory => [...prevInventory, newItem]);
      }
    }
    setOpenDialog(false);
  };

  // Render mobile card view
  const renderMobileView = () => (
    <Box>
      {filteredInventory.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography>No items found. Add items using the "Add Item" button.</Typography>
        </Paper>
      ) : (
        filteredInventory.map((item) => (
          <Card key={item.id} sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" component="div">
                {item.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                SKU: {item.sku}
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    Price: ${item.price.toFixed(2)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    Quantity: {item.quantity}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    Category: {item.category}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    Location: {item.location}
                  </Typography>
                </Grid>
              </Grid>
              {item.quantity <= item.reorderPoint && (
                <Chip 
                  icon={<WarningIcon />} 
                  label="Low Stock" 
                  color="warning" 
                  size="small" 
                  sx={{ mt: 1 }} 
                />
              )}
            </CardContent>
            <Divider />
            <CardActions>
              <Button size="small" onClick={() => handleEditItem(item)}>Edit</Button>
            </CardActions>
          </Card>
        ))
      )}
    </Box>
  );

  // Render desktop table view
  const renderTableView = () => (
    <TableContainer component={Paper}>
      {filteredInventory.length === 0 ? (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography>No items found. Add items using the "Add Item" button.</Typography>
        </Box>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>SKU</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Category</TableCell>
              <TableCell align="right">Price</TableCell>
              <TableCell align="right">Quantity</TableCell>
              <TableCell>Location</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredInventory.map((item) => (
              <TableRow 
                key={item.id}
                sx={{ 
                  backgroundColor: item.quantity <= item.reorderPoint ? 'rgba(255, 152, 0, 0.1)' : 'inherit'
                }}
              >
                <TableCell>{item.sku}</TableCell>
                <TableCell>{item.name}</TableCell>
                <TableCell>{item.category}</TableCell>
                <TableCell align="right">${item.price.toFixed(2)}</TableCell>
                <TableCell align="right">
                  {item.quantity}
                  {item.quantity <= item.reorderPoint && (
                    <WarningIcon 
                      fontSize="small" 
                      color="warning" 
                      sx={{ ml: 1, verticalAlign: 'middle' }} 
                    />
                  )}
                </TableCell>
                <TableCell>{item.location}</TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => handleEditItem(item)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </TableContainer>
  );

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Inventory Management
      </Typography>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Search Inventory"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select
                value={categoryFilter}
                label="Category"
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <MenuItem value="all">All Categories</MenuItem>
                {categories.map(category => (
                  <MenuItem key={category} value={category}>{category}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Stock</InputLabel>
              <Select
                value={stockFilter}
                label="Stock"
                onChange={(e) => setStockFilter(e.target.value)}
              >
                <MenuItem value="all">All Stock</MenuItem>
                <MenuItem value="low">Low Stock</MenuItem>
                <MenuItem value="out">Out of Stock</MenuItem>
                <MenuItem value="in">In Stock</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid 
            item 
            xs={12} 
            sm={6} 
            md={4} 
            sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}
          >
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddItem}
              sx={{ mr: 1 }}
            >
              Add Item
            </Button>
            <Button
              variant="outlined"
              startIcon={<ScanIcon />}
              onClick={() => navigate('/staff/store/inventory/scan')}
            >
              Scan
            </Button>
          </Grid>
        </Grid>
      </Paper>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="inventory tabs">
          <Tab label="All Items" />
          <Tab label="Low Stock" />
        </Tabs>
      </Box>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2">
              {filteredInventory.length} items found
            </Typography>
          </Box>
          
          {isMobile ? renderMobileView() : renderTableView()}
        </>
      )}
      {/* Edit/Add Item Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {currentItem?.id ? 'Edit Item' : 'Add New Item'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="SKU"
                value={currentItem?.sku || ''}
                onChange={(e) => setCurrentItem(prev => prev ? {...prev, sku: e.target.value} : null)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Name"
                value={currentItem?.name || ''}
                onChange={(e) => setCurrentItem(prev => prev ? {...prev, name: e.target.value} : null)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={currentItem?.category || ''}
                  label="Category"
                  onChange={(e) => setCurrentItem(prev => prev ? {...prev, category: e.target.value} : null)}
                >
                  {categories.map(category => (
                    <MenuItem key={category} value={category}>{category}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Location"
                value={currentItem?.location || ''}
                onChange={(e) => setCurrentItem(prev => prev ? {...prev, location: e.target.value} : null)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Price"
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                value={currentItem?.price || ''}
                onChange={(e) => setCurrentItem(prev => prev ? {...prev, price: parseFloat(e.target.value)} : null)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Cost"
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                value={currentItem?.cost || ''}
                onChange={(e) => setCurrentItem(prev => prev ? {...prev, cost: parseFloat(e.target.value)} : null)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Quantity"
                type="number"
                value={currentItem?.quantity || ''}
                onChange={(e) => setCurrentItem(prev => prev ? {...prev, quantity: parseInt(e.target.value)} : null)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Reorder Point"
                type="number"
                value={currentItem?.reorderPoint || ''}
                onChange={(e) => setCurrentItem(prev => prev ? {...prev, reorderPoint: parseInt(e.target.value)} : null)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveItem} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InventoryPage; 